import { useEffect, useState } from 'react';

// =====================================================================
// chart-theme — the ONLY place charts read styling from (Phase 8b).
// Recharts' defaults are the generic-SaaS fingerprint .impeccable.md bans
// (indigo lines, grey gridlines, boxy white tooltips, rainbow pies). Every
// value here resolves to a brand chart token declared in styles.css (--chart-*),
// so charts inherit the brand instead of the library defaults.
// =====================================================================

export const CHART = {
  line: 'var(--chart-line)',
  areaFrom: 'var(--chart-area-from)',
  areaTo: 'var(--chart-area-to)',
  point: 'var(--chart-point)',
  // Sequential brand ramp for parts-of-whole (ordered by magnitude). NOT a
  // categorical rainbow — order encodes size.
  cat: ['var(--chart-cat-1)', 'var(--chart-cat-2)', 'var(--chart-cat-3)', 'var(--chart-cat-4)'],
  catOther: 'var(--chart-cat-other)',
  bar: 'var(--chart-bar)',
  barTrack: 'var(--chart-bar-track)',
  grid: 'var(--chart-grid)',
  axis: 'var(--chart-axis)',
  up: 'var(--chart-up)',
  down: 'var(--chart-down)',
  flat: 'var(--chart-flat)',
} as const;

/** Slice color for a parts-of-whole segment: brand ramp by rank, neutral "Other". */
export function sliceColor(key: string, index: number): string {
  if (key === 'other') return CHART.catOther;
  return CHART.cat[index % CHART.cat.length] ?? CHART.catOther;
}

// Quiet axis + grid defaults: horizontal gridlines only, no axis lines, no
// vertical grid, brand-tertiary tick text. Spread onto Recharts primitives.
export const gridProps = {
  stroke: CHART.grid,
  strokeDasharray: '0',
  vertical: false,
} as const;

export const axisProps = {
  stroke: 'transparent',
  tickLine: false,
  axisLine: false,
  tick: { fill: 'var(--color-text-tertiary)', fontSize: 12 },
} as const;

/** True when the user asked the OS to reduce motion (Recharts animates in JS,
 *  so the global CSS media query can't reach it — gate `isAnimationActive`). */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}

interface TooltipEntry {
  name?: string;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
  /** Optional formatter for the header label (e.g. pretty date). */
  labelFormatter?: (label: string | number) => string;
}

// Custom tooltip — replaces Recharts' default white box with a card styled from
// our tokens: soft shadow, brand border, DM Sans, tabular-nums figures.
export function ChartTooltip({ active, payload, label, labelFormatter }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md">
      {label !== undefined && label !== '' ? (
        <p className="mb-1 text-xs font-medium text-text-tertiary">
          {labelFormatter ? labelFormatter(label) : label}
        </p>
      ) : null}
      <div className="flex flex-col gap-0.5">
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: entry.color ?? CHART.line }}
            />
            <span className="text-text-secondary">{entry.name}</span>
            <span className="ml-auto font-semibold tabular-nums text-foreground">
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
